import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CurrentThdChart from "../components/CurrentThdChart";
import MeasurementChart from "../components/MeasurementChart";
import MeasurementCard from "../components/MeasurementCard";
import CurrentsReportDialog from "../components/CurrentsReportDialog";

function breakLargeGaps(data, maxGapMs = 60 * 60 * 1000) {
  if (!data.length) return [];
  const result = [data[0]];
  for (let i = 1; i < data.length; i++) {
    const prev = data[i - 1];
    const curr = data[i];
    const gap = curr.time - prev.time;
    if (gap > maxGapMs) {
      result.push({ time: prev.time + 1, I1: null, I2: null, I3: null, IN: null });
      result.push({ time: curr.time - 1, I1: null, I2: null, I3: null, IN: null });
    }
    result.push(curr);
  }
  return result;
}

function CurrentsPage({ deviceType = "analyzer" }) {
  const { t } = useTranslation();
  const { deviceId } = useParams(); // ✅ BUNU EKLE

  const saved = JSON.parse(localStorage.getItem("selectedDevice") || "{}");
  const isVirtual = saved.mode === "virtual";
  const targetId = isVirtual ? saved.virtualId : deviceId;

  const [currentData, setCurrentData] = useState([]);
  const [currentMax, setCurrentMax] = useState({});
  const [currentMin, setCurrentMin] = useState({});
  const [thdData, setThdData] = useState({});
  const [range, setRange] = useState("1D");
  const [reportOpen, setReportOpen] = useState(false);


  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const ENDPOINT_PREFIX = isVirtual
    ? "/virtual"
    : deviceType === "gruparge"
      ? "/gruparge"
      : deviceType === "reactive"
        ? "/reactive"
        : deviceType === "multimeter"
          ? "/multimeter"
          : "/measurements";



  function getRangeMs(r) {
    switch (r) {
      case "1H": return 1 * 60 * 60 * 1000;
      case "3H": return 3 * 60 * 60 * 1000;
      case "6H": return 6 * 60 * 60 * 1000;
      case "12H": return 12 * 60 * 60 * 1000;
      case "1D": return 1 * 24 * 60 * 60 * 1000;
      case "3D": return 3 * 24 * 60 * 60 * 1000;
      case "1W": return 7 * 24 * 60 * 60 * 1000;
      default: return 1 * 24 * 60 * 60 * 1000;
    }
  }

  async function fetchCurrents() {
    try {
      const end = new Date();

      const start = new Date(end.getTime() - getRangeMs(range));
      const url = `${API_BASE}${ENDPOINT_PREFIX}/currents?deviceId=${targetId}&start=${start.toISOString()}&end=${end.toISOString()}`;
      const res = await fetch(url);
      const rows = await res.json();
      const formatted = rows.map((r) => ({
        time: new Date(r.time).getTime(),
        I1: r.I1,
        I2: r.I2,
        I3: r.I3,
        IN: r.IN ?? null, // Grup Arge'de IN yok
      }));
      setCurrentData(breakLargeGaps(formatted));
    } catch (err) {
      console.error("fetchCurrents error:", err);
    }
  }

  async function fetchCurrentMax() {
    const res = await fetch(`${API_BASE}${ENDPOINT_PREFIX}/current-max?deviceId=${targetId}`);
    setCurrentMax(await res.json());
  }

  async function fetchCurrentMin() {
    const res = await fetch(`${API_BASE}${ENDPOINT_PREFIX}/current-min?deviceId=${targetId}`);
    setCurrentMin(await res.json());
  }

  async function fetchThd() {
    const res = await fetch(`${API_BASE}${ENDPOINT_PREFIX}/current-thd?deviceId=${targetId}`);
    setThdData(await res.json());
  }

  useEffect(() => {
    if (!targetId) return;
    fetchCurrents();
    const i = setInterval(fetchCurrents, 5000);
    return () => clearInterval(i);
  }, [API_BASE, targetId, range]);

  // Max/Min sadece analyzer için (Grup Arge'de max/min yok)
  useEffect(() => {
    if (!targetId || deviceType === "reactive" || deviceType === "multimeter" || deviceType === "gruparge" || isVirtual) return;
    fetchCurrentMax();
    const i = setInterval(fetchCurrentMax, 10000);
    return () => clearInterval(i);
  }, [API_BASE, targetId, deviceType]);

  // Max/Min (Grup Arge'de max/min yok)
  useEffect(() => {
    if (!targetId || deviceType === "reactive" || deviceType === "multimeter" || deviceType === "gruparge" || isVirtual) return;
    fetchCurrentMin();
    const i = setInterval(fetchCurrentMin, 10000);
    return () => clearInterval(i);
  }, [API_BASE, targetId, deviceType]);

  // THD (Grup Arge'de THD yok)
  useEffect(() => {
    if (!targetId || deviceType === "multimeter" || deviceType === "gruparge") return;
    fetchThd();
    const i = setInterval(fetchThd, 10000);
    return () => clearInterval(i);
  }, [API_BASE, targetId, deviceType]);

  return (
    <div className="p-4 md:p-6 flex flex-col gap-6">

      <div className="bg-white rounded-2xl shadow-md p-4 md:p-6 flex flex-col">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">{t('currents.currents')}</h2>
        <MeasurementChart
          data={currentData}
          range={range}
          onRangeChange={setRange}
          type="current"
          deviceType={deviceType}
        />
      </div>

      {/* Max/Min sadece analyzer için (Grup Arge'de yok) */}
      {deviceType !== "reactive" &&
        !isVirtual &&
        deviceType !== "multimeter" &&
        deviceType !== "gruparge" && (
          <div className="flex justify-center">
            <MeasurementCard
              title={t('currents.currentMaxMin')}
              data={{ ...currentMax, ...currentMin }}
              type="current"
              unit="A"
            />
          </div>
        )}



      {/* THD - Grup Arge'de yok */}
      {deviceType !== "multimeter" && deviceType !== "gruparge" && (
        <div className="bg-white rounded-2xl shadow-md p-4 md:p-6 flex flex-col">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            {t('currents.thdI')}
          </h2>
          <CurrentThdChart data={thdData} />
        </div>
      )}


      <div className="flex items-center justify-start md:col-span-2 mb-2">
        <button
          onClick={() => setReportOpen(true)}
          className="bg-sky-600 text-white px-3 py-2 rounded-md hover:bg-sky-700 text-sm"
        >
          {t('currents.reportButton')}
        </button>
      </div>

      <CurrentsReportDialog
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        deviceId={targetId}
        deviceType={deviceType}
        API_BASE={API_BASE}
        isVirtual={isVirtual}
      />
    </div>
  );
}

export default CurrentsPage;
